import { Component, Injector, ViewChild, OnInit } from '@angular/core';
import { BaseFormComponent } from '../base-form.component';
import { BasicTextInputComponent } from '../basic-text-input/basic-text-input.component';
import { Subscription } from 'rxjs';
import { AmorphicService } from '../../../../services/amorphic/amorphic.service';
import { CoverpathUtils } from '../../../../../js/static/CoverpathUtils';
import { CheckboxBasicComponent } from '../basic-checkbox/basic-checkbox.component';
import { TextAutoCompleteAddressComponent } from '../address-autocomplete/address-autocomplete.component';

@Component({
  moduleId: module.id,
  selector: 'custom-address-long',
  templateUrl: './custom-address-long.component.html',
})
export class CustomAddressLongComponent extends BaseFormComponent {
  @ViewChild('streetRef') streetTextInput: TextAutoCompleteAddressComponent;
  @ViewChild('line2Ref') line2TextInput: BasicTextInputComponent;
  @ViewChild('zipRef') zipTextInput: BasicTextInputComponent;
  @ViewChild('cityRef') cityTextInput: BasicTextInputComponent;
  @ViewChild('stateRef') stateTextInput: BasicTextInputComponent;
  @ViewChild('prefillOwnerCheckbox') prefillOwnerCheckbox: CheckboxBasicComponent;
  zip: string;
  city: string;
  state: string;
  zipChangeSubscription: Subscription;
  insuredOrOwner: boolean;
  amorphicService: AmorphicService;
  zipKept: boolean;
  checkboxText: string;
  showCheckbox: boolean;

  constructor(protected injector: Injector) {
    super(injector);

    this.amorphicService = injector.get(AmorphicService);
    this.questionItem = this.injector.get('questionItem');
    this.prop = this.handleIterate(this.questionItem.prop);
    this.checkboxText = this.questionItem.optionalInputLabel;
    this.showCheckbox = !!this.checkboxText;

    this.insuredOrOwner =
      this.prop === 'customer.application.primaryPolicy.insured.address' ||
      CoverpathUtils.isOwnerAddressProp(this.prop);

    if (this.insuredOrOwner) {
      this.zipChangeSubscription = this.geoChangeService.geoChangeSource$.subscribe((event) => {
        this.handleGeoChange(event);
      });
    }

    this.setVisibility();
    this.initializeFromModel();
  }

  initializeFromModel() {
    this.zip = this.getValue(this.prop + '.zip');
    this.city = this.getValue(this.prop + '.city');
    this.state = this.getValue(this.prop + '.state');
  }

  async ngAfterViewInit() {
    if (this.partnerFillService.partnerPropertiesMap) {
      // Check if the A360 state matches the contract state on the application. If yes,
      // fill in zipCode, line1, city. If not, leave this component untouched
      if (this.insuredOrOwner) {
        const quoteState = this.amorphicService.controller.customer.application.primaryPolicy.getApplicationState();
        const partnerOwnerState =
          this.partnerFillService.partnerPropertiesMap[
            'customer.application.primaryPolicy.ownerPersonResidentialAddress.state'
          ];
        const partnerApplicationData = this.amorphicService.controller.partnerPrefillDataObject.applicationData;

        const city = this.getValue(this.prop + '.city');
        if (!city) {
          if (CoverpathUtils.isOwnerAddressProp(this.prop)) {
            if (
              quoteState === partnerOwnerState &&
              partnerApplicationData.owner &&
              partnerApplicationData.owner.address
            ) {
              // Fill the rest of the address
              this.streetTextInput.setValue(this.prop + '.street', partnerApplicationData.owner.address.line1);
              this.line2TextInput.setValue(this.prop + '.line1', partnerApplicationData.owner.address.line2);
              this.zipTextInput.setValue(this.prop + '.zip', partnerApplicationData.owner.address.postalCode);
              this.cityTextInput.setValue(this.prop + '.city', partnerApplicationData.owner.address.city);
              this.setZip(partnerApplicationData.owner.address.postalCode);
            }
          } else if (partnerApplicationData.insured && partnerApplicationData.insured.primaryAddress) {
            this.streetTextInput.setValue(this.prop + '.street', partnerApplicationData.insured.primaryAddress.line1);
            this.line2TextInput.setValue(this.prop + '.line1', partnerApplicationData.insured.primaryAddress.line2);
            this.zipTextInput.setValue(this.prop + '.zip', partnerApplicationData.insured.primaryAddress.postalCode);
            this.cityTextInput.setValue(this.prop + '.city', partnerApplicationData.insured.primaryAddress.city);
            this.setZip(partnerApplicationData.insured.primaryAddress.postalCode);
          }
        }
      }
    }
  }

  updateFromModel() {
    const zip = this.getValue(this.prop + '.zip');
    //If Zip Code changes, get new City and State for Zip
    if (zip !== this.zip) {
      if (!!zip) {
        this.setZip(zip);
      } else {
        this.resetValue();
      }
    }
  }

  private async handleGeoChange(response): Promise<void> {
    /*
        3 possible results from changing zip code

        1 - user changes zip, is prompted to change app, leaves app
                no further action here, this component is destroyed
                This function is not even invoked
        2 - user changes zip, is prompted to change app, chooses to stay
                replace newly typed zip with original zip
                case: zipKept
        3 - user changes zip, does not require prompt to leave app
                just place new data into inputs
                case: newZip
        */
    if (response.prop === this.prop) {
      switch (response.event) {
        case 'zipKept':
          // old zip was never replaced
          // just force it into child text input
          this.zipTextInput.val = this.zip;
          this.zipKept = true;
          break;
        case 'newZip':
          // update zip value and associative fields(state, city etc.)
          await this.validateAndSetZip(response.newZip, response.zipLookup);
          this.zipKept = false;
          break;
      }
    }
  }

  async setZip(newZip) {
    try {
      const zipLookup = await this.geoChangeService.zipCodeLookup(newZip);

      /**
       * if owner zip code changed
       * or
       * insured zip code changed AND insured = owner
       * then check if metadata reload is required
       */
      const policy = this.amorphicService.controller.customer.application.primaryPolicy;
      if (
        this.insuredOrOwner &&
        (CoverpathUtils.isOwnerAddressProp(this.prop) ||
          !policy.ownerType ||
          policy.isOwnerSameAsInsured() ||
          (policy.isJointOwnership() && policy.isJointOwnerSomeoneElse !== 'true'))
      ) {
        if (await this.unsupportedUseCasesService.isUnsupportedUseCase(this.prop + '.state', zipLookup['stateCode'])) {
          return;
        }
        this.geoChangeService.handleApplicationZipCodeChange(
          this.prop,
          newZip,
          this.zip,
          'application-form',
          zipLookup
        );
      } else {
        await this.validateAndSetZip(newZip, zipLookup);
      }
    } catch (e) {
      this.onError(e);
    }
  }

  private async validateAndSetZip(newZip: string, zipLookup: object) {
    try {
      this.zip = newZip;
      this.city = zipLookup['city'];
      this.state = zipLookup['stateCode'];
      await this.validateAndSet(this.prop + '.zip', newZip);
      await this.validateAndSet(this.prop + '.city', this.city);
      await this.validateAndSet(this.prop + '.state', this.state);
      await this.validateAndSet(this.prop + '.latitude', zipLookup['lat'].toString());
      await this.validateAndSet(this.prop + '.longitude', zipLookup['lng'].toString());
      await this.validateAndSet(this.prop + '.timezone', zipLookup['timezone']);
      this.geoChangeService.stateChangeSource.next(); //for update filling number
      if (this.isError()) {
        this.errorMessage[this.prop] = '';
      }
    } catch (e) {
      this.onError(e);
    }
  }

  isError(): boolean {
    return !!this.errorMessage[this.prop];
  }

  /**
   * Error handler
   */
  onError(e) {
    this.appSvc.submitValueAndFireTriggers(`${this.prop}.zip`, undefined);
    this.questionItem.needsFixed = true;
    this.zipTextInput.errorMessage[this.zipTextInput.prop] = e;
    this.zipTextInput.isError = true;
    this.updateProgressAndSubscriptions();
  }

  resetValue() {
    this.zip = null;
    this.city = null;
    this.state = null;
  }

  getErrorMessage(): string {
    return this.errorMessage[this.prop];
  }

  onCheckboxChange() {
    this.prefillOwnerCheckbox.updateFromModel();
  }
}
